import React from 'react'
import Select from 'react-select'
import { TimeRange } from 'models'
import { getSelectStyles, CustomControl, ValueContainer, DropdownIndicator, CustomOption } from 'components/global/Selects/SingleSelectStyles'
import { useTranslation } from 'react-i18next'
import { useTheme, useMediaQuery } from '@mui/material'

export interface TimeRangeSelectChoice {
  label: string
  value: TimeRange
  id: number
}

interface TimeRangeSelectProps {
  value: TimeRange
  onChange: (value: TimeRangeSelectChoice) => void
  choices?: TimeRangeSelectChoice[]
  selectedChoice?: TimeRangeSelectChoice
  isSmall?: boolean
  disabled?: boolean
}

const TimeRangeSelect: React.FC<TimeRangeSelectProps> = ({ 
  value, 
  onChange, 
  choices,
  selectedChoice,
  isSmall = false,
  disabled = false
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.between('lg', 'xl'))

  const defaultChoices: TimeRangeSelectChoice[] = [
    {
      label: t('stats.range_select.last_30_days'),
      value: TimeRange.LastThirtyDays,
      id: 1,
    },
    {
      label: t('stats.range_select.last_month'),
      value: TimeRange.LastMonth,
      id: 5,
    },
    {
      label: t('stats.range_select.last_3_months'),
      value: TimeRange.LastThreeMonths,
      id: 6,
    },
    {
      label: t('stats.range_select.current_year'),
      value: TimeRange.LastYear,
      id: 2,
    },
    {
      label: t('stats.range_select.last_12_months'),
      value: TimeRange.LastTwelveMonths,
      id: 3,
    },
    {
      label: t('stats.range_select.last_3_years'),
      value: TimeRange.LastThreeYears,
      id: 4,
    }
  ]

  const finalChoices = choices || defaultChoices
  const finalSelectedChoice = selectedChoice || finalChoices.find((choice) => choice.value === value)

  const handleChange = (newValue: TimeRangeSelectChoice | null) => {
    if (newValue && !disabled) {
      onChange(newValue)
    }
  }

  const responsiveSelectStyles = {
    ...getSelectStyles(theme, isSmall),
    control: (provided, state) => ({
      ...provided,
      minHeight: isSmall ? '32px' : '40px',
      height: 'auto',
      fontSize: isSmall ? (isLargeScreen ? '10px' : '9px') : '12px',
      minWidth: isSmall ? (isLargeScreen ? '140px' : '120px') : 'auto',
      display: 'flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      border: disabled 
        ? '1px solid #ccc' 
        : `1px solid ${state.isFocused ? theme.palette.primary.main : '#EFEFEF'}`,
      whiteSpace: 'nowrap',
      backgroundColor: 'white',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: 1
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      opacity: disabled ? 0.5 : 1
    }),
    menu: (provided) => ({
      ...provided,
      display: disabled ? 'none' : 'block'
    })
  }

  return (
    <Select
      value={finalSelectedChoice}
      onChange={handleChange}
      options={finalChoices}
      placeholder={t('stats.range_select.placeholder')}
      components={{
        Control: CustomControl,
        ValueContainer: ValueContainer,
        DropdownIndicator: DropdownIndicator,
        Option: CustomOption,
        IndicatorSeparator: () => null,
      }}
      styles={responsiveSelectStyles}
      isSearchable={false}
      isDisabled={false}
      menuIsOpen={disabled ? false : undefined}
      onMenuOpen={() => disabled ? null : undefined}
      menuPlacement="auto"
      menuPortalTarget={document.body}
    />
  )
}

export default TimeRangeSelect